/**
 * Sesion de la UI.
 *
 * El flujo completo es: Google entrega un ID token -> se guarda en el
 * `token-store` -> la API lo verifica y devuelve `{ email, name, role }`. El rol
 * lo decide **siempre** la API; aca solo se guarda para esconder acciones que el
 * usuario no puede ejecutar (ver `roles.ts`).
 *
 * El provider escucha al `token-store`: si un 401 en cualquier request limpia el
 * token, la sesion cae sola a `anonymous` y el router manda al login. Asi no hay
 * dos fuentes de verdad sobre si hay sesion o no.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { UnauthorizedError } from '../api/errors';
import type { SessionUser } from '../api/types';
import { useMonitorApi } from '../app/api-context';
import { disableGoogleAutoSelect } from './google';
import { can, type Permission } from './roles';
import { clearToken, getToken, setToken, subscribeToToken } from './token-store';

export type AuthStatus = 'checking' | 'anonymous' | 'authenticated' | 'error';

export interface AuthState {
  readonly status: AuthStatus;
  readonly user: SessionUser | null;
  /** Falla al resolver la sesion (API caida, contrato roto). No incluye el 401. */
  readonly error: unknown;
  /** Guarda el ID token y dispara la resolucion de la sesion. */
  signIn(idToken: string): void;
  signOut(): void;
  /** Vuelve a preguntar por la sesion (util tras un error transitorio). */
  refresh(): void;
}

interface SessionSnapshot {
  readonly status: AuthStatus;
  readonly user: SessionUser | null;
  readonly error: unknown;
}

const ANONYMOUS: SessionSnapshot = { status: 'anonymous', user: null, error: null };

const AuthContext = createContext<AuthState | null>(null);

export interface AuthProviderProps {
  readonly children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  const api = useMonitorApi();
  const [snapshot, setSnapshot] = useState<SessionSnapshot>(() =>
    getToken() === null ? ANONYMOUS : { status: 'checking', user: null, error: null },
  );
  const [nonce, setNonce] = useState(0);

  // Cualquier cambio del token (login, logout, 401 en otra pantalla) revalida.
  useEffect(() => subscribeToToken(() => setNonce((value) => value + 1)), []);

  useEffect(() => {
    if (getToken() === null) {
      setSnapshot(ANONYMOUS);
      return;
    }
    let cancelled = false;
    setSnapshot((previous) => ({ ...previous, status: 'checking', error: null }));
    api.getSession().then(
      (user) => {
        if (!cancelled) {
          setSnapshot({ status: 'authenticated', user, error: null });
        }
      },
      (error: unknown) => {
        if (cancelled) {
          return;
        }
        if (error instanceof UnauthorizedError) {
          // Token vencido o rechazado: se vuelve al login sin ruido.
          clearToken();
          setSnapshot(ANONYMOUS);
          return;
        }
        // Cualquier otra falla se muestra: no se puede quedar en blanco.
        setSnapshot({ status: 'error', user: null, error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [api, nonce]);

  const signIn = useCallback((idToken: string): void => {
    setToken(idToken);
  }, []);

  const signOut = useCallback((): void => {
    disableGoogleAutoSelect();
    clearToken();
  }, []);

  const refresh = useCallback((): void => {
    setNonce((value) => value + 1);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status: snapshot.status,
      user: snapshot.user,
      error: snapshot.error,
      signIn,
      signOut,
      refresh,
    }),
    [snapshot, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const auth = useContext(AuthContext);
  if (auth === null) {
    throw new Error('useAuth requiere que el arbol este dentro de <AuthProvider>.');
  }
  return auth;
}

/**
 * Permiso del usuario actual.
 *
 * Es control de UI, no de seguridad: la API vuelve a validar cada accion. Sirve
 * para no ofrecer un boton que terminaria en 403.
 */
export function useCan(permission: Permission): boolean {
  const { user } = useAuth();
  return can(user?.role, permission);
}
